import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import {
  RiArrowDownSLine,
  RiArrowRightSLine,
  RiEditLine,
} from "@remixicon/react";
import { useState } from "react";
import type { FeedProductSalesProduct } from "@/modules/cf-feed/presentation/api/types";
import { useBoundedInfiniteScroll } from "@/modules/cf-feed/presentation/lib/use-bounded-infinite-scroll";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { formatKip, Thumb } from "./table-helpers";

export type FeedSalesTableProps = {
  products: FeedProductSalesProduct[];
  isLoading?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  onEditSpecialWork: (orderId: string) => void;
  mode?: "actual" | "cf";
};

function orderStatusVariant(
  status: string,
): "default" | "secondary" | "outline" | "destructive" {
  if (status === "cancelled") return "destructive";
  if (status === "delivered" || status === "paid") return "default";
  if (status === "pending") return "secondary";
  return "outline";
}

export function FeedSalesTable({
  products,
  isLoading,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  onEditSpecialWork,
  mode = "cf",
}: FeedSalesTableProps) {
  const { scrollRootRef, sentinelRef } = useBoundedInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
  });
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(
    () => new Set(),
  );
  const [expandedVariants, setExpandedVariants] = useState<Set<string>>(
    () => new Set(),
  );

  return (
    <div className="space-y-3">
      <div
        ref={scrollRootRef}
        className="max-h-[36rem] overflow-auto overscroll-contain rounded-md border"
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ສິນຄ້າ / ຕົວເລືອກ</TableHead>
              <TableHead className="text-right">
                {mode === "actual" ? "ຂາຍຈິງ" : "ຈຳນວນ CF"}
              </TableHead>
              <TableHead className="text-right">ງານພິມ</TableHead>
              <TableHead className="text-right">ພ້ອມ</TableHead>
              <TableHead className="text-right">ລໍຖ້າ</TableHead>
              <TableHead className="text-right">
                {mode === "actual" ? "ຍອດຂາຍ" : "ມູນຄ່າ CF"}
              </TableHead>
              <TableHead>ລູກຄ້າ / ສະຖານະ</TableHead>
              <TableHead>ເວລາ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-28 text-center">
                  {mode === "actual"
                    ? "ກຳລັງໂຫຼດຍອດຂາຍຈິງ..."
                    : "ກຳລັງໂຫຼດລາຍການ CF..."}
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="h-28 text-center text-muted-foreground"
                >
                  {mode === "actual" ? "ຍັງບໍ່ມີຍອດຂາຍຈິງ" : "ຍັງບໍ່ມີລາຍການ CF"}
                </TableCell>
              </TableRow>
            ) : (
              products.flatMap((product) => {
                const productOpen = expandedProducts.has(product.productId);
                const productRow = (
                  <TableRow key={`product-${product.productId}`}>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-auto w-full justify-start gap-2 p-0 text-left"
                        aria-expanded={productOpen}
                        onClick={() =>
                          setExpandedProducts((current) => {
                            const next = new Set(current);
                            if (next.has(product.productId)) {
                              next.delete(product.productId);
                            } else {
                              next.add(product.productId);
                            }
                            return next;
                          })
                        }
                      >
                        {productOpen ? (
                          <RiArrowDownSLine className="size-4 shrink-0" />
                        ) : (
                          <RiArrowRightSLine className="size-4 shrink-0" />
                        )}
                        <Thumb src={product.imageUrl} />
                        <span className="min-w-0">
                          <span className="block truncate font-medium">
                            {product.title}
                          </span>
                          <span className="block text-muted-foreground text-xs">
                            {product.variants.length} ຕົວເລືອກ
                          </span>
                        </span>
                      </Button>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {product.quantity}
                    </TableCell>
                    <TableCell className="text-right">
                      {product.specialQuantity}
                    </TableCell>
                    <TableCell className="text-right text-emerald-600 dark:text-emerald-400">
                      {product.readyQuantity}
                    </TableCell>
                    <TableCell className="text-right text-amber-600 dark:text-amber-400">
                      {product.pendingQuantity}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatKip(product.totalPrice)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      —
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      —
                    </TableCell>
                  </TableRow>
                );
                if (!productOpen) return [productRow];

                const variantRows = product.variants.flatMap((variant) => {
                  const variantKey = `${product.productId}:${variant.variantId}`;
                  const variantOpen = expandedVariants.has(variantKey);
                  const variantRow = (
                    <TableRow
                      key={`variant-${variantKey}`}
                      className="bg-muted/30"
                    >
                      <TableCell className="pl-10">
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-auto justify-start gap-2 p-0 text-left"
                          aria-expanded={variantOpen}
                          onClick={() =>
                            setExpandedVariants((current) => {
                              const next = new Set(current);
                              if (next.has(variantKey)) next.delete(variantKey);
                              else next.add(variantKey);
                              return next;
                            })
                          }
                        >
                          {variantOpen ? (
                            <RiArrowDownSLine className="size-4" />
                          ) : (
                            <RiArrowRightSLine className="size-4" />
                          )}
                          <span>
                            {variant.label} · {variant.orders.length} ອໍເດີ້
                          </span>
                        </Button>
                      </TableCell>
                      <TableCell className="text-right">
                        {variant.quantity}
                      </TableCell>
                      <TableCell className="text-right">
                        {variant.specialQuantity}
                      </TableCell>
                      <TableCell className="text-right">
                        {variant.readyQuantity}
                      </TableCell>
                      <TableCell className="text-right">
                        {variant.pendingQuantity}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatKip(variant.totalPrice)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        —
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        —
                      </TableCell>
                    </TableRow>
                  );
                  if (!variantOpen) return [variantRow];
                  return [
                    variantRow,
                    ...variant.orders.map((order) => (
                      <TableRow key={`order-${order.orderId}`}>
                        <TableCell className="pl-16">
                          <span className="font-mono text-xs">
                            CF {order.cfCode}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {order.quantity}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {order.specialQuantity ?? 0}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {order.readyQuantity ?? 0}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {order.pendingQuantity ?? 0}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatKip(order.totalPrice)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-2">
                            <span>{order.customerName}</span>
                            <Badge variant={orderStatusVariant(order.status)}>
                              {order.statusLabel}
                            </Badge>
                            {(order.specialQuantity ?? 0) > 0 ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-7 gap-1"
                                onClick={() => onEditSpecialWork(order.orderId)}
                              >
                                <RiEditLine className="size-3.5" />
                                ງານພິມ
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {formatDateTimeLocal(order.createdAt)}
                        </TableCell>
                      </TableRow>
                    )),
                  ];
                });
                return [productRow, ...variantRows];
              })
            )}
          </TableBody>
        </Table>
        {hasNextPage ? (
          <div
            ref={sentinelRef}
            className="flex min-h-10 items-center justify-center text-muted-foreground text-xs"
            aria-live="polite"
          >
            {isFetchingNextPage ? "ກຳລັງໂຫຼດເພີ່ມ..." : "ເລື່ອນເພື່ອໂຫຼດເພີ່ມ"}
          </div>
        ) : null}
      </div>
    </div>
  );
}
